/**
 * useInteractiveRunner
 * ====================
 * Connects to the Interactive Runner WebSocket at port 8001.
 * Streams real-time node status events during flow test-runs.
 *
 * Usage:
 *   const { run, cancel, isRunning } = useInteractiveRunner({ onMessage });
 *   await run(graph, tenant_id);
 */

import { useRef, useState, useCallback } from 'react';

const INTERACTIVE_RUNNER_URL =
  import.meta.env.VITE_INTERACTIVE_RUNNER_URL ||
  'ws://localhost:8001';

export type RunnerEventType =
  | 'NODE_STATUS'      // node_start / node_complete / node_error (from executor callbacks)
  | 'EXECUTION_COMPLETE'
  | 'EXECUTION_PAUSED'
  | 'ERROR'
  | 'CONNECTED';

export interface RunnerMessage {
  type: RunnerEventType;
  payload?: any;     // Mirrors executor callback payload
  status?: string;
  message?: string;
}

interface UseInteractiveRunnerOptions {
  /** Called for every message received from the runner */
  onMessage: (msg: RunnerMessage) => void;
  /** Called when the run finishes (success or error) */
  onComplete?: (success: boolean) => void;
}

export function useInteractiveRunner({ onMessage, onComplete }: UseInteractiveRunnerOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  /** Open WS, send graph, stream events */
  const run = useCallback(
    async (graph: any, tenant_id: string, rowLimit?: number, targetNodeId?: string) => {
      const runId = `run-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Close any existing connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      setIsRunning(true);

      return new Promise<string>((resolve, reject) => {
        const url = `${INTERACTIVE_RUNNER_URL}/ws/run/${runId}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          onMessage({ type: 'CONNECTED', payload: { runId } });
          // Send the graph immediately after connect
          ws.send(JSON.stringify({ tenant_id, graph, row_limit: rowLimit, target_node_id: targetNodeId }));
        };

        ws.onmessage = (event) => {
          try {
            const msg: RunnerMessage = JSON.parse(event.data);
            onMessage(msg);

            if (msg.type === 'EXECUTION_COMPLETE' || msg.type === 'EXECUTION_PAUSED') {
              setIsRunning(false);
              onComplete?.(true);
              resolve(runId);
            } else if (msg.type === 'ERROR') {
              setIsRunning(false);
              // Don't call onComplete here — reject() is caught by runFlow's try/catch
              // which shows the single error notification. Calling both would double-toast.
              reject(new Error(msg.message || 'Runner error'));
            }
          } catch {
            // Non-JSON message — ignore
          }
        };

        ws.onerror = () => {
          setIsRunning(false);
          // Don't call onComplete here — reject() is caught by runFlow's try/catch
          // which shows the single error notification. Calling both would double-toast.
          reject(new Error('Interactive Runner connection failed'));
        };

        ws.onclose = () => {
          // If we weren't already marked done, the connection dropped unexpectedly
          setIsRunning(false);
        };
      });
    },
    [onMessage, onComplete]
  );

  /** Cancel in-progress run */
  const cancel = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setIsRunning(false);
  }, []);

  return { run, cancel, isRunning };
}
